import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, ChevronDown, ExternalLink, FileSearch, Globe } from "lucide-react";

// National multi-state aggregator portals — cover thousands of state, city & county agencies.
const AGGREGATORS = [
  { name: "SAM.gov (Federal)", url: "https://sam.gov/", note: "Federal contracts & grants" },
  { name: "BidNet Direct", url: "https://www.bidnetdirect.com/", note: "State & local bids nationwide" },
  { name: "DemandStar", url: "https://www.demandstar.com/", note: "Free local government bids" },
  { name: "Bonfire", url: "https://gobonfire.com/find-bids/", note: "Public-sector RFPs" },
  { name: "Periscope S2G / mySmartGov", url: "https://www.periscopeholdings.com/", note: "State eProcurement network" },
  { name: "OpenGov / ProcureNow", url: "https://opengov.com/products/procurement/", note: "Municipal procurement" },
  { name: "GovWin IQ (Deltek)", url: "https://iq.govwin.com/", note: "Market intel (paid)" },
];

// Statewide procurement portals for all 50 states + DC.
const STATES = {
  "Alabama": [{ name: "Alabama STAARS / Purchasing", url: "https://purchasing.alabama.gov/" }],
  "Alaska": [{ name: "Alaska Online Public Notices", url: "https://aws.state.ak.us/OnlinePublicNotices/" }],
  "Arizona": [{ name: "ProcureAZ / APP", url: "https://app.az.gov/" }],
  "Arkansas": [{ name: "Arkansas State Procurement", url: "https://www.dfa.arkansas.gov/procurement/" }],
  "California": [
    { name: "Cal eProcure", url: "https://caleprocure.ca.gov/" },
    { name: "Caltrans Contracts", url: "https://dot.ca.gov/programs/procurement-and-contracts" },
  ],
  "Colorado": [{ name: "Colorado VSS / BIDS", url: "https://www.colorado.gov/vss" }],
  "Connecticut": [{ name: "CT Source / DAS Procurement", url: "https://portal.ct.gov/DAS/Procurement/" }],
  "Delaware": [{ name: "Delaware MMP / Bid Solicitation", url: "https://mymarketplace.delaware.gov/" }],
  "District of Columbia": [{ name: "DC OCP / Contracts", url: "https://ocp.dc.gov/page/solicitations" }],
  "Florida": [
    { name: "MyFloridaMarketPlace / VBS", url: "https://vendor.myfloridamarketplace.com/" },
    { name: "Florida DMS Purchasing", url: "https://www.dms.myflorida.com/business_operations/state_purchasing" },
  ],
  "Georgia": [{ name: "Georgia Procurement Registry (GPR)", url: "https://ssl.doas.state.ga.us/gpr/" }],
  "Hawaii": [{ name: "Hawaii eProcurement (HIePRO)", url: "https://hiepro.ehawaii.gov/" }],
  "Idaho": [{ name: "Idaho Luma / Purchasing", url: "https://purchasing.idaho.gov/" }],
  "Illinois": [{ name: "Illinois BidBuy / Procurement Bulletin", url: "https://www.bidbuy.illinois.gov/" }],
  "Indiana": [{ name: "Indiana IDOA Procurement", url: "https://www.in.gov/idoa/procurement/" }],
  "Iowa": [{ name: "Iowa Bid Opportunities", url: "https://bidopportunities.iowa.gov/" }],
  "Kansas": [{ name: "Kansas eSupplier / Procurement", url: "https://admin.ks.gov/offices/procurement-and-contracts" }],
  "Kentucky": [{ name: "Kentucky eProcurement", url: "https://finance.ky.gov/services/eprocurement" }],
  "Louisiana": [{ name: "LaPAC / LaGov", url: "https://wwwcfprd.doa.louisiana.gov/osp/lapac/pubmain.cfm" }],
  "Maine": [{ name: "Maine Division of Procurement", url: "https://www.maine.gov/dafs/bbm/procurementservices/vendors/rfps" }],
  "Maryland": [{ name: "eMaryland Marketplace (eMMA)", url: "https://procurement.maryland.gov/" }],
  "Massachusetts": [{ name: "COMMBUYS", url: "https://www.commbuys.com/" }],
  "Michigan": [{ name: "Michigan SIGMA VSS", url: "https://sigma.michigan.gov/" }],
  "Minnesota": [{ name: "Minnesota SWIFT Supplier", url: "https://mn.gov/admin/osp/" }],
  "Mississippi": [{ name: "Mississippi MAGIC / Procurement", url: "https://www.dfa.ms.gov/dfa-offices/mmrs/" }],
  "Missouri": [{ name: "MissouriBUYS", url: "https://missouribuys.mo.gov/" }],
  "Montana": [{ name: "Montana eMACS", url: "https://emacs.mt.gov/" }],
  "Nebraska": [{ name: "Nebraska Procurement / Bid Opportunities", url: "https://das.nebraska.gov/materiel/purchasing.html" }],
  "Nevada": [{ name: "Nevada NEATS / Purchasing", url: "https://nevadaepro.com/" }],
  "New Hampshire": [{ name: "NH Bureau of Purchasing", url: "https://www.das.nh.gov/purchasing/" }],
  "New Jersey": [{ name: "NJSTART", url: "https://www.njstart.gov/" }],
  "New Mexico": [{ name: "New Mexico State Purchasing", url: "https://www.generalservices.state.nm.us/state-purchasing/" }],
  "New York": [
    { name: "NY State Contract Reporter", url: "https://www.nyscr.ny.gov/" },
    { name: "NYC City Record Online", url: "https://a856-cityrecord.nyc.gov/" },
  ],
  "North Carolina": [{ name: "NC eVP / IPS", url: "https://www.ips.state.nc.us/" }],
  "North Dakota": [{ name: "North Dakota Bidder Registration", url: "https://www.omb.nd.gov/vendor" }],
  "Ohio": [{ name: "Ohio Procurement / OhioBuys", url: "https://procure.ohio.gov/" }],
  "Oklahoma": [{ name: "Oklahoma OMES Purchasing", url: "https://oklahoma.gov/omes/services/purchasing.html" }],
  "Oregon": [{ name: "OregonBuys", url: "https://oregonbuys.gov/" }],
  "Pennsylvania": [{ name: "PA eMarketplace", url: "https://www.emarketplace.state.pa.us/" }],
  "Rhode Island": [{ name: "Rhode Island Division of Purchases", url: "https://www.ridop.ri.gov/" }],
  "South Carolina": [{ name: "SC Procurement Services", url: "https://procurement.sc.gov/" }],
  "South Dakota": [{ name: "South Dakota Bureau of Procurement", url: "https://bfm.sd.gov/bid/" }],
  "Tennessee": [{ name: "Tennessee Edison / SWC Bids", url: "https://www.tn.gov/generalservices/procurement.html" }],
  "Texas": [
    { name: "Texas SmartBuy / ESBD", url: "http://www.txsmartbuy.com/esbd" },
    { name: "Comptroller — Statewide Procurement", url: "https://comptroller.texas.gov/purchasing/" },
    { name: "TxDOT — Bid/Proposal Lobby", url: "https://www.txdot.gov/business/contractors-consultants.html" },
    { name: "BuyBoard Co-op", url: "https://www.buyboard.com/" },
  ],
  "Utah": [{ name: "Utah SciQuest / Purchasing", url: "https://purchasing.utah.gov/" }],
  "Vermont": [{ name: "Vermont Bid Opportunities", url: "https://bgs.vermont.gov/purchasing-contracting/bids" }],
  "Virginia": [{ name: "eVA Virginia", url: "https://eva.virginia.gov/" }],
  "Washington": [{ name: "Washington WEBS", url: "https://pr-webs-vendor.des.wa.gov/" }],
  "West Virginia": [{ name: "West Virginia wvOASIS / VSS", url: "https://prod.wvoasis.gov/" }],
  "Wisconsin": [{ name: "Wisconsin VendorNet", url: "https://vendornet.wi.gov/" }],
  "Wyoming": [{ name: "Wyoming Procurement", url: "https://ai.wyo.gov/divisions/general-services/procurement-section" }],
};

const STATE_NAMES = Object.keys(STATES);
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "");

export const StateLocalDirectory = () => {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState("Texas");
  const navigate = useNavigate();
  const links = STATES[state] || [];

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="state-local-directory">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="state-local-toggle"
        className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-lg bg-emerald-900 text-white flex items-center justify-center shrink-0">
            <MapPin className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="font-heading font-semibold text-slate-900">State &amp; local procurement directory</p>
            <p className="text-sm text-slate-500 truncate">Official portals for all 50 states + national bid aggregators — then score or track any find here.</p>
          </div>
        </div>
        <ChevronDown className={`h-5 w-5 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-slate-100 p-5 space-y-6" data-testid="state-local-content">
          {/* National aggregators */}
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 inline-flex items-center gap-1.5">
              <Globe className="h-3.5 w-3.5" /> National &amp; multi-state portals
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {AGGREGATORS.map((l) => (
                <a
                  key={l.name}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`agg-link-${slug(l.name)}`}
                  className="group flex flex-col gap-0.5 rounded-lg border border-slate-100 bg-slate-50 p-3 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors"
                >
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 group-hover:text-emerald-700">
                    {l.name}
                    <ExternalLink className="h-3.5 w-3.5 text-slate-300 group-hover:text-emerald-600 shrink-0" />
                  </span>
                  <span className="text-xs text-slate-400">{l.note}</span>
                </a>
              ))}
            </div>
          </div>

          {/* State picker */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Pick your state</p>
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                data-testid="state-select"
                className="rounded-md border border-slate-200 px-3 py-1.5 text-sm max-w-[260px] focus:outline-none focus:ring-2 focus:ring-accent/40"
              >
                {STATE_NAMES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <ul className="grid sm:grid-cols-2 gap-2" data-testid="state-links">
              {links.map((l) => (
                <li key={l.name}>
                  <a
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`state-link-${slug(l.name)}`}
                    className="group inline-flex items-start gap-1.5 text-sm text-slate-600 hover:text-emerald-700 transition-colors"
                  >
                    <span className="leading-snug">{l.name}</span>
                    <ExternalLink className="h-3.5 w-3.5 mt-0.5 text-slate-300 group-hover:text-emerald-600 shrink-0" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="text-xs text-slate-400">Found a contract? Paste its link or PDF into Score Any Bid to get an instant fit score.</p>
            <button
              onClick={() => navigate("/dashboard/triage")}
              data-testid="state-local-go-score-button"
              className="shrink-0 bg-emerald-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-emerald-800 transition-colors inline-flex items-center gap-2"
            >
              <FileSearch className="h-4 w-4" /> Score a found bid
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
